import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ShoppingCart } from "lucide-react";

// Components
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import TrustBar from "@/components/TrustBar";
import ProductsGrid from "@/components/ProductsGrid";
import Footer from "@/components/Footer";
import CartModal from "@/components/CartModal";
import LeadFormModal from "@/components/LeadFormModal";
import AccessibilityWidget from "@/components/AccessibilityWidget";
import ProductPage from "@/components/ProductPage";
import UnitOnlyGrid from "@/components/UnitOnlyGrid";
import InstallationGrid from "@/components/InstallationGrid";
import InstallationProductPage from "@/components/InstallationProductPage";
import LocationPage from "@/components/LocationPage";
import EstimateFormModal from "@/components/EstimateFormModal";
import WhyGoodmanPage from "@/components/WhyGoodmanPage";
import SizingChartPage from "@/components/SizingChartPage";
import { AuthProvider, useAuth } from "@/components/AuthContext";
import AdminLogin from "@/components/AdminLogin";
import AdminDashboard from "@/components/AdminDashboard";

// SEO Component
const SEOHead = ({ title, description }) => {
  useEffect(() => {
    document.title = title;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
  }, [title, description]);
  return null;
};

// Dynamic CMS Page component - reads content from /api/site-content
const API_URL_PUBLIC = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CmsPage = ({ pageKey, seoTitle, seoDescription, fallbackTitle, fallbackContent }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL_PUBLIC}/site-content`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.[pageKey]?.content) setContent(data[pageKey]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pageKey]);

  const title = content?.title || fallbackTitle;
  const body = content?.content;

  // Render content: supports HTML or plain text (auto-converts newlines to paragraphs)
  const renderContent = (text) => {
    if (!text) return null;
    // If it looks like HTML (contains tags), render as HTML
    if (/<[a-z][\s\S]*>/i.test(text)) {
      return <div dangerouslySetInnerHTML={{ __html: text }} />;
    }
    // Otherwise treat as plain text - split by double newlines into paragraphs
    return text.split(/\n\n+/).map((para, i) => (
      <p key={i} className="text-[#333333] mb-4">{para.split('\n').map((line, j) => (
        <span key={j}>{line}{j < para.split('\n').length - 1 && <br />}</span>
      ))}</p>
    ));
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead title={seoTitle} description={seoDescription} />
      <Header onOpenLeadForm={() => {}} />
      <main className="max-w-4xl mx-auto px-4 py-20">
        <h1 className="font-heading text-4xl font-black mb-8">{title}</h1>
        <article className="prose prose-lg">
          {body ? renderContent(body) : fallbackContent}
        </article>
      </main>
      <Footer />
      <AccessibilityWidget />
    </div>
  );
};

// Fallback content for each page (used when CMS content is empty)
const TermsPage = () => (
  <CmsPage
    pageKey="terms"
    seoTitle="Terms & Conditions | Appliance Direct HVAC"
    seoDescription="Terms and conditions for Appliance Direct HVAC services in Central Florida."
    fallbackTitle="Terms and Conditions"
    fallbackContent={
      <>
        <p className="text-[#333333] mb-4">Welcome to Appliance Direct. By accessing our website and services, you agree to these terms.</p>
        <h2 className="font-heading font-bold text-2xl mt-8 mb-4">1. Services</h2>
        <p className="text-[#333333] mb-4">Appliance Direct provides HVAC system sales and installation through two separate, carefully selected, licensed and insured companies to provide installation services in Central Florida.</p>
        <h2 className="font-heading font-bold text-2xl mt-8 mb-4">2. Pricing</h2>
        <p className="text-[#333333] mb-4">All prices are subject to change without notice. Unit Only prices include Condenser, Air Handler and Heating Element and free pick up at any of our 10 Central Florida locations. We can arrange delivery for a small fee.</p>
        <p className="text-[#333333] mb-4">Units purchased through Appliance Direct and installed by one of our licensed and insured partners, include Condenser, Air Handler, Heating Element, Thermostat, standard installation, all permits and a 10 Year Factory Warranty.</p>
        <h2 className="font-heading font-bold text-2xl mt-8 mb-4">3. Warranty</h2>
        <p className="text-[#333333] mb-4">All HVAC systems purchased through Appliance Direct and installed by one of our licensed and insured partners, include a 10 Year Factory Warranty. Installation is performed by our installation partners, Florida Breeze (LIC#CAC-1814113) or Strada Services LLC (LIC#CAC-043953).</p>
        <h2 className="font-heading font-bold text-2xl mt-8 mb-4">4. Contact</h2>
        <p className="text-[#333333]">For questions about these terms, please contact us at info@appliancedirect.com.</p>
      </>
    }
  />
);

const PrivacyPage = () => (
  <CmsPage
    pageKey="privacy"
    seoTitle="Privacy Policy | Appliance Direct HVAC"
    seoDescription="Privacy policy for Appliance Direct HVAC. Learn how we collect, use, and protect your information."
    fallbackTitle="Privacy Policy"
    fallbackContent={
      <>
        <p className="text-[#333333] mb-4">Your privacy is important to us. This policy explains how we collect, use, and protect your information.</p>
        <h2 className="font-heading font-bold text-2xl mt-8 mb-4">Information We Collect</h2>
        <p className="text-[#333333] mb-4">We collect information you provide directly, such as name, email, phone number, and address when you submit a lead form or make a purchase.</p>
        <h2 className="font-heading font-bold text-2xl mt-8 mb-4">How We Use Information</h2>
        <p className="text-[#333333] mb-4">We use your information to process orders, schedule installations, and communicate about our services.</p>
        <h2 className="font-heading font-bold text-2xl mt-8 mb-4">Data Protection</h2>
        <p className="text-[#333333]">We implement appropriate security measures to protect your personal information.</p>
      </>
    }
  />
);

const AccessibilityPage = () => (
  <CmsPage
    pageKey="accessibility"
    seoTitle="Accessibility Statement | Appliance Direct HVAC"
    seoDescription="Appliance Direct HVAC accessibility statement. We are committed to ensuring digital accessibility for people with disabilities."
    fallbackTitle="Website Accessibility Statement"
    fallbackContent={
      <>
        <p className="text-sm text-gray-500 mb-8">Last Updated: February 27, 2026</p>
        <section className="mb-8">
          <h2 className="font-heading font-bold text-2xl mb-4">Our Commitment to Accessibility</h2>
          <p className="text-[#333333]">We are committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying relevant accessibility standards to enhance usability and inclusivity.</p>
        </section>
        <section className="mb-8">
          <h2 className="font-heading font-bold text-2xl mb-4">Accessibility Standards</h2>
          <p className="text-[#333333]">We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA. Accessibility is an ongoing process, and we are actively working toward improving the accessibility and usability of our website.</p>
        </section>
        <section className="mb-8">
          <h2 className="font-heading font-bold text-2xl mb-4">Feedback & Assistance</h2>
          <p className="text-[#333333]"><strong>Contact:</strong> info@appliancedirect.com or call 1-844-203-5519</p>
        </section>
      </>
    }
  />
);

// Main Landing Page
const LandingPage = ({ cartItems, setCartItems, showCart, setShowCart, showLeadForm, setShowLeadForm, showEstimateForm, setShowEstimateForm, selectedProduct, setSelectedProduct }) => {
  const handleAddToCart = (product) => {
    const existingItem = cartItems.find(item => item.id === product.id);
    if (existingItem) {
      setCartItems(prev => prev.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCartItems(prev => [...prev, { ...product, quantity: 1 }]);
    }
  };

  const handleRequestQuote = (product) => {
    setSelectedProduct(product);
    setShowLeadForm(true);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <SEOHead 
        title="AC Installation Near Me | Appliance Direct HVAC | Central Florida's #1 Heat Pump Deals" 
        description="Looking for AC installation near me? Appliance Direct offers professional HVAC installation in Central Florida. New AC unit installation, air conditioner installation near me, and home AC installation with Goodman systems. 10 Year Factory Warranty across Brevard, Orange, Osceola, Seminole, Polk, Lake, Marion, Sumter, Putnam, Volusia & Flagler Counties."
      />
      <Header onOpenLeadForm={() => setShowLeadForm(true)} onOpenEstimateForm={() => setShowEstimateForm(true)} />
      <Hero setShowForm={setShowLeadForm} />
      <TrustBar />

      {/* Product Categories */}
      <section id="products" className="bg-gray-50 py-20" data-testid="products-section">
        <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <span className="inline-block bg-emerald-100 text-emerald-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              Shop Our Systems
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Premium Goodman Heat Pump Systems</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Choose how you want to purchase your HVAC system</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Installation Card */}
            <a href="/installation" className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col" data-testid="category-installation">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-10 flex items-center justify-center">
                <img src="https://customer-assets.emergentagent.com/job_cooling-converter/artifacts/udt89p1i_2.webp" alt="AC Installation Near Me - Professional HVAC Installation in Central Florida" className="h-48 object-contain" />
              </div>
              <div className="p-8 flex flex-col flex-1">
                <h3 className="text-2xl font-bold mb-2 group-hover:text-emerald-700 transition-colors" style={{ color: '#DC2626' }}>Professional Installation</h3>
                <p className="text-gray-600 mb-2 flex-1">AC installation near me by licensed and insured technicians. New AC unit installation includes all equipment, labor, permits and <span className="font-bold text-lg" style={{ color: '#DC2626' }}>10 Year Factory Warranty!</span></p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-3">
                  <div>
                    <span className="font-black text-3xl" style={{ color: '#DC2626' }}>From $98/mo</span>
                    <p className="text-sm font-semibold" style={{ color: '#DC2626' }}>with No Payments for 6 months</p>
                  </div>
                  <span className="text-gray-700 font-semibold flex-shrink-0">Click Here.</span>
                </div>
              </div>
            </a>

            {/* Unit Only Card */}
            <a href="/unit-only" className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col" data-testid="category-unit-only">
              <div className="bg-gradient-to-br from-emerald-100 to-emerald-200 p-10 flex items-center justify-center">
                <img src="https://customer-assets.emergentagent.com/job_cooling-converter/artifacts/udt89p1i_2.webp" alt="New AC Unit - Goodman Heat Pump System for Home AC Installation" className="h-48 object-contain" />
              </div>
              <div className="p-8 flex flex-col flex-1">
                <h3 className="text-2xl font-bold mb-2 group-hover:text-emerald-700 transition-colors" style={{ color: '#DC2626' }}>Unit Only</h3>
                <p className="text-gray-600 mb-1">Purchase the equipment at our best online price.</p>
                <p className="font-semibold" style={{ color: '#DC2626' }}>Air Handler, Condenser and Heating Element included.</p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-4">
                  <span className="font-black text-3xl" style={{ color: '#DC2626' }}>0% Interest Available</span>
                  <span className="text-gray-700 font-semibold flex-shrink-0">Click Here.</span>
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>
      <Footer />
      <AccessibilityWidget />
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
};

function AppContent() {
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showEstimateForm, setShowEstimateForm] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const handleAddToCart = (product) => {
    const existingItem = cartItems.find(item => item.id === product.id);
    if (existingItem) {
      setCartItems(prev => prev.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCartItems(prev => [...prev, { ...product, quantity: 1 }]);
    }
  };

  const handleRequestQuote = (product) => {
    setSelectedProduct(product);
    setShowLeadForm(true);
  };

  return (
    <>
      <Routes>
        <Route path="/" element={
          <LandingPage
            cartItems={cartItems} setCartItems={setCartItems}
            showCart={showCart} setShowCart={setShowCart}
            showLeadForm={showLeadForm} setShowLeadForm={setShowLeadForm}
            showEstimateForm={showEstimateForm} setShowEstimateForm={setShowEstimateForm}
            selectedProduct={selectedProduct} setSelectedProduct={setSelectedProduct}
          />
        } />
        <Route path="/unit-only" element={
          <UnitOnlyGrid
            onAddToCart={handleAddToCart}
            onOpenLeadForm={() => setShowLeadForm(true)}
            onOpenEstimateForm={() => setShowEstimateForm(true)}
            cartItems={cartItems}
            onOpenCart={() => setShowCart(true)}
          />
        } />
        <Route path="/unit-only/:id" element={
          <ProductPage
            onAddToCart={handleAddToCart}
            onRequestQuote={handleRequestQuote}
            onOpenLeadForm={() => setShowLeadForm(true)}
            onOpenEstimateForm={() => setShowEstimateForm(true)}
            cartItems={cartItems}
            onOpenCart={() => setShowCart(true)}
          />
        } />
        <Route path="/installation" element={
          <InstallationGrid
            onRequestQuote={handleRequestQuote}
            onOpenLeadForm={() => setShowLeadForm(true)}
            onOpenEstimateForm={() => setShowEstimateForm(true)}
          />
        } />
        <Route path="/installation/:id" element={
          <InstallationProductPage
            onRequestQuote={handleRequestQuote}
            onOpenLeadForm={() => setShowLeadForm(true)}
            onOpenEstimateForm={() => setShowEstimateForm(true)}
          />
        } />
        {/* Legacy route redirect */}
        <Route path="/products/:id" element={
          <ProductPage
            onAddToCart={handleAddToCart}
            onRequestQuote={handleRequestQuote}
            onOpenLeadForm={() => setShowLeadForm(true)}
            onOpenEstimateForm={() => setShowEstimateForm(true)}
            cartItems={cartItems}
            onOpenCart={() => setShowCart(true)}
          />
        } />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/accessibility" element={<AccessibilityPage />} />
        <Route path="/why-goodman" element={
          <WhyGoodmanPage onOpenLeadForm={() => setShowLeadForm(true)} onOpenEstimateForm={() => setShowEstimateForm(true)} />
        } />
        <Route path="/sizing-chart" element={
          <SizingChartPage onOpenLeadForm={() => setShowLeadForm(true)} onOpenEstimateForm={() => setShowEstimateForm(true)} />
        } />
        <Route path="/ac-installation/:slug" element={
          <LocationPage onOpenLeadForm={() => setShowLeadForm(true)} onOpenEstimateForm={() => setShowEstimateForm(true)} />
        } />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      </Routes>

      {/* Floating Cart Button - visible on all pages */}
      {cartItems.length > 0 && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-6 right-6 bg-emerald-600 hover:bg-emerald-700 text-white p-4 rounded-full z-40 shadow-lg flex items-center gap-2 transition-colors"
          data-testid="floating-cart-btn"
        >
          <ShoppingCart className="w-5 h-5" />
          <span className="font-semibold">{cartItems.length}</span>
        </button>
      )}

      {/* Global Modals */}
      <CartModal 
        isOpen={showCart}
        onClose={() => setShowCart(false)}
        cartItems={cartItems}
        setCartItems={setCartItems}
      />
      <LeadFormModal 
        isOpen={showLeadForm}
        onClose={() => {
          setShowLeadForm(false);
          setSelectedProduct(null);
        }}
        selectedProduct={selectedProduct}
      />
      <EstimateFormModal
        isOpen={showEstimateForm}
        onClose={() => setShowEstimateForm(false)}
      />

      <Toaster position="top-right" richColors />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
